import React, { useState, useEffect } from 'react';
import { ArrowUpDown, ArrowUp, ArrowDown, Loader2 } from 'lucide-react';

type OrderData = {
  date_activation: string;
  country: string;
  campaign: string;
  platform?: string;
  campaign_id?: string;
  campaign_original?: string;
  historic_coeff?: number | null;
  arppu: number;
  ltv: number;
  predicted_ltv: number;
  cpa_cost_per_action: number;
  cpu_cost_per_user: number;
  roi: number;
  predicted_roi: number;
  revenue_activation_count: number;
  revenue_activation_purchase_count: number | null;
  revenue_activation: number | null;
  revenue_p1: number | null;
  revenue_p1_count: number | null;
  revenue_p2: number | null;
  revenue_p2_count: number | null;
  revenue_p3: number | null;
  revenue_p3_count: number | null;
  revenue_p4: number | null;
  revenue_p4_count: number | null;
  revenue_p5: number | null;
  revenue_p5_count: number | null;
  revenue_p6: number | null;
  revenue_p6_count: number | null;
  revenue_p7: number | null;
  revenue_p7_count: number | null;
  revenue_p8: number | null;
  revenue_p8_count: number | null;
  revenue_p9: number | null;
  revenue_p9_count: number | null;
  revenue_p10: number | null;
  revenue_p10_count: number | null;
  revenue_p11: number | null;
  revenue_p11_count: number | null;
  revenue_p12: number | null;
  revenue_p12_count: number | null;
  predicted_revenue_p1?: number | null;
  predicted_revenue_p2?: number | null;
  predicted_revenue_p3?: number | null;
  predicted_revenue_p4?: number | null;
  predicted_revenue_p5?: number | null;
  predicted_revenue_p6?: number | null;
  predicted_revenue_p7?: number | null;
  predicted_revenue_p8?: number | null;
  predicted_revenue_p9?: number | null;
  predicted_revenue_p10?: number | null;
  predicted_revenue_p11?: number | null;
  predicted_revenue_p12?: number | null;
  early_predicted_revenue_p1?: number | null;
  early_predicted_revenue_p2?: number | null;
  early_predicted_revenue_p3?: number | null;
  early_predicted_revenue_p4?: number | null;
  early_predicted_revenue_p5?: number | null;
  early_predicted_revenue_p6?: number | null;
  early_predicted_revenue_p7?: number | null;
  early_predicted_revenue_p8?: number | null;
  early_predicted_revenue_p9?: number | null;
  early_predicted_revenue_p10?: number | null;
  early_predicted_revenue_p11?: number | null;
  early_predicted_revenue_p12?: number | null;
  predicted_churn_p1?: number | null;
  predicted_churn_p2?: number | null;
  predicted_churn_p3?: number | null;
  predicted_churn_p4?: number | null;
  predicted_churn_p5?: number | null;
  predicted_churn_p6?: number | null;
  predicted_churn_p7?: number | null;
  predicted_churn_p8?: number | null;
  predicted_churn_p9?: number | null;
  predicted_churn_p10?: number | null;
  predicted_churn_p11?: number | null;
  predicted_churn_p12?: number | null;
  count_unique_payers?: number | null;
  count_unique_payers_activation?: number | null;
  costs: number | null;
  total_revenue: number | null;
  total_predicted_revenue?: number | null;
};

type GroupedOrderData = {
  [date: string]: {
    [country: string]: OrderData[];
  };
};

type AggregatedData = {
  [key: string]: OrderData;
};

type TotalsComponentProps = {
  dateFrom: string;
  dateTo: string;
  selectedPlatforms: string[];
  selectedCountries?: string[];
  selectedCampaigns?: string[];
  showCountries: boolean;
  showCampaigns: boolean;
  showWithTax: boolean;
  viewMode: 'daily' | 'monthly';
  hideAllPeriods: boolean;
  showEarlyPredicted: boolean;
  vatRates: { [country: string]: number };
  distributeUnknowns?: boolean;
  distributeDirect?: boolean;
};

type SortConfig = {
  key: string;
  direction: 'asc' | 'desc' | 'none';
};

const TotalsComponent: React.FC<TotalsComponentProps> = ({
  dateFrom, 
  dateTo, 
  selectedCountries = [], 
  selectedCampaigns = [], 
  selectedPlatforms,
  showCountries, 
  showCampaigns,
  showWithTax,
  viewMode,
  hideAllPeriods,
  showEarlyPredicted,
  vatRates,
  distributeUnknowns = false,
  distributeDirect = false
}) => {
  const [ordersData, setOrdersData] = useState<GroupedOrderData>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: '',
    direction: 'none'
  });

  // =================================================================
  // БЛОК 1: БАЗОВЫЕ ФУНКЦИИ РАСЧЕТОВ
  // =================================================================

  const getNumericValue = (value: any): number => {
    if (value === null || value === undefined || value === '') {
      return 0;
    }
    const num = parseFloat(String(value));
    return isNaN(num) ? 0 : num;
  };

  // VAT functions
  const getVatRate = (country: string): number => {
    return vatRates[country] !== undefined ? vatRates[country] : 0.0;
  };

  const getAdjustedRevenue = (value: number | null, country: string): number => {
    const numericValue = getNumericValue(value);
    if (!showWithTax) return numericValue;
    
    const vatRate = getVatRate(country);
    return numericValue * (1 - vatRate);
  };

  const getHistoricCoeff = (item: OrderData): number => {
    return getNumericValue(item.historic_coeff) || 1;
  };

  // Calculate early predicted revenue functions
  const calculateEarlyPredictedRevenueArray = (item: OrderData): (number | null)[] => {
    const earlyPredictedRevenueArray: (number | null)[] = [];
    
    let currentValue = getNumericValue(item.revenue_p1);
    earlyPredictedRevenueArray[0] = currentValue;

    for (let i = 2; i <= 12; i++) {
      const churnKey = `predicted_churn_p${i}` as keyof OrderData;
      const churnValue = item[churnKey];

      if (currentValue === null || currentValue === 0 || churnValue === null || churnValue === undefined) {
        earlyPredictedRevenueArray[i - 1] = null;
        currentValue = 0;
      } else {
        currentValue = currentValue * (getNumericValue(churnValue) / 100.0); 
        earlyPredictedRevenueArray[i - 1] = currentValue;
      }
    }

    return earlyPredictedRevenueArray;
  };

  const enrichOrderDataWithEarlyPredicted = (item: OrderData): OrderData => {
    const earlyPredictedArray = calculateEarlyPredictedRevenueArray(item);
    
    const enrichedItem = { ...item };
    
    for (let i = 1; i <= 12; i++) {
      const fieldKey = `early_predicted_revenue_p${i}` as keyof OrderData;
      (enrichedItem[fieldKey] as any) = earlyPredictedArray[i - 1];
    }
    
    return enrichedItem;
  };
  
  const calculateTotalRevenue = (item: OrderData): number => {
  if (getNumericValue(item.total_revenue) > 0) {
      // ИСПРАВЛЕНИЕ: применяем VAT корректировку к готовому total_revenue
      return getAdjustedRevenue(getNumericValue(item.total_revenue), item.country);
  }
  
  let total = getAdjustedRevenue(getNumericValue(item.revenue_activation), item.country);
  for (let i = 1; i <= 12; i++) {
      const revenueKey = `revenue_p${i}` as keyof OrderData;
      total += getAdjustedRevenue(getNumericValue((item as any)[revenueKey]), item.country);
  }
  return total;
};

  const calculateTotalPredictedRevenue = (item: OrderData): number => {
    if (getNumericValue(item.total_predicted_revenue) > 0) {
        return getAdjustedRevenue(getNumericValue(item.total_predicted_revenue), item.country);
    }
    let total = getAdjustedRevenue(getNumericValue(item.revenue_activation), item.country);
    for (let i = 1; i <= 12; i++) {
        const key = `predicted_revenue_p${i}` as keyof OrderData;
        total += getAdjustedRevenue(getNumericValue((item as any)[key]), item.country);
    }
    return total;
  };

  const calculatePredictedRevenue2 = (item: OrderData): number => {
  if (getNumericValue(item.total_predicted_revenue) > 0) {
      // ИСПРАВЛЕНИЕ: применяем VAT корректировку
      return getAdjustedRevenue(getNumericValue(item.total_predicted_revenue), item.country);
  }
  
  return calculateTotalPredictedRevenue(item);
};

  // ИСПРАВЛЕННАЯ функция calculateEarlyPredictedRevenue
  const calculateEarlyPredictedRevenue = (item: OrderData): number => {
  // Проверяем, агрегированные ли это данные
  const isAggregatedData = item.country === 'All' || item.campaign === 'Total' || item.campaign === item.country;
  
  if (isAggregatedData) {
    // Для агрегированных данных правильное значение уже сохранено в специальном поле
    const aggregatedValue = (item as any).aggregated_early_predicted_revenue || 0;
    // ИСПРАВЛЕНИЕ: применяем VAT корректировку к агрегированному значению
    return getAdjustedRevenue(aggregatedValue, item.country);
  } else {
    // Для отдельных записей используем historic_coeff
    const totalRevenue = calculateTotalRevenue(item); // уже с VAT корректировкой
    const historicCoeff = getHistoricCoeff(item);
    
    if (historicCoeff <= 1.0) {
      const adjustment = (historicCoeff - 1.0) * 0.1;
      return totalRevenue * (1 + adjustment);
    } else {
      const adjustment = (historicCoeff - 1.0) * 0.1;
      return totalRevenue * (1 + adjustment);
    }
  }
};

  const calculateEarlyPredictedLTV = (item: OrderData): number => {
    const earlyPredictedRevenue = calculateEarlyPredictedRevenue(item);
    const adjustedEarlyPredicted = getAdjustedRevenue(earlyPredictedRevenue, item.country);
    const activationCount = getNumericValue(item.count_unique_payers_activation);
    return activationCount > 0 ? adjustedEarlyPredicted / activationCount : 0;
  };

  const calculateEarlyPredictedROI = (item: OrderData): number => {
    const earlyPredictedRevenue = calculateEarlyPredictedRevenue(item);
    const adjustedEarlyPredicted = getAdjustedRevenue(earlyPredictedRevenue, item.country);
    const costs = getNumericValue(item.costs);
    return costs > 0 ? ((adjustedEarlyPredicted - costs) / costs) * 100 : 0;
  };

  const calculateARPPU = (item: OrderData): number => {
    const totalRevenue = calculateTotalRevenue(item);
    const uniquePayers = getNumericValue(item.count_unique_payers);
    return uniquePayers > 0 ? totalRevenue / uniquePayers : 0;
  };

  const calculateLTV = (item: OrderData): number => {
    const totalRevenue = calculateTotalRevenue(item);
    const activationCount = getNumericValue(item.count_unique_payers_activation);
    return activationCount > 0 ? totalRevenue / activationCount : 0;
  };

  const calculatePredictedLTV = (item: OrderData): number => {
    const predictedRevenue = calculatePredictedRevenue2(item);
    const activationCount = getNumericValue(item.count_unique_payers_activation);
    return activationCount > 0 ? predictedRevenue / activationCount : 0;
  };

  const calculateCPU = (item: OrderData): number => {
    const costs = getNumericValue(item.costs);
    const uniquePayers = getNumericValue(item.count_unique_payers);
    return uniquePayers > 0 ? costs / uniquePayers : 0;
  };

  const calculateCPA = (item: OrderData): number => {
    const costs = getNumericValue(item.costs);
    const activationPurchases = getNumericValue(item.revenue_activation_purchase_count);
    return activationPurchases > 0 ? costs / activationPurchases : 0;
  };

  const calculateROI = (item: OrderData): number => {
    const totalRevenue = calculateTotalRevenue(item);
    const costs = getNumericValue(item.costs);
    return costs > 0 ? ((totalRevenue - costs) / costs) * 100 : 0;
  };

  const calculatePredictedROI = (item: OrderData): number => {
    const predictedRevenue = calculateTotalPredictedRevenue(item);
    const costs = getNumericValue(item.costs);
    return costs > 0 ? ((predictedRevenue - costs) / costs) * 100 : 0;
  };

  const getColumnValue = (item: OrderData, column: string): number => {
    switch(column) {
      case 'revenue_activation':
        return getAdjustedRevenue(getNumericValue(item.revenue_activation), item.country);
      case 'costs':
        return getNumericValue(item.costs);
      case 'total_revenue':
        return calculateTotalRevenue(item);
      case 'profit':
        return calculateTotalRevenue(item) - getNumericValue(item.costs);
      case 'roi':
        return calculateROI(item);
      case 'predicted_roi':
        return calculatePredictedROI(item);
      case 'early_predicted_roi':
        return calculateEarlyPredictedROI(item);
      case 'total_early_predicted_revenue':
        return calculateEarlyPredictedRevenue(item); // уже с VAT корректировкой внутри
      case 'cpa_cost_per_action':
        return calculateCPA(item);
      case 'cpu_cost_per_user':
        return calculateCPU(item);
      case 'ltv':
        return calculateLTV(item);
      case 'predicted_ltv':
        return calculatePredictedLTV(item);
      case 'early_predicted_ltv':
        return calculateEarlyPredictedLTV(item);
      case 'arppu':
        return calculateARPPU(item);
      case 'total_predicted_revenue':
        return calculatePredictedRevenue2(item);
      case 'revenue_activation_count':
        return getNumericValue(item.revenue_activation_count);
      case 'revenue_activation_purchase_count':
        return getNumericValue(item.revenue_activation_purchase_count);
      case 'count_unique_payers':
        return getNumericValue(item.count_unique_payers);
      case 'count_unique_payers_activation':
        return getNumericValue(item.count_unique_payers_activation);
      case 'revenue_p1': case 'revenue_p2': case 'revenue_p3': case 'revenue_p4':
      case 'revenue_p5': case 'revenue_p6': case 'revenue_p7': case 'revenue_p8':
      case 'revenue_p9': case 'revenue_p10': case 'revenue_p11': case 'revenue_p12':
        return getAdjustedRevenue(getNumericValue(item[column as keyof OrderData]), item.country);
      default:
        return 0;
    }
  };

  // =================================================================
  // БЛОК 2: ИСПРАВЛЕННАЯ ФУНКЦИЯ АГРЕГАЦИИ
  // =================================================================

  const aggregateItems = (items: OrderData[], country: string): OrderData => {
    const aggregated: OrderData = {
      date_activation: `${dateFrom} - ${dateTo}`,
      country: country,
      campaign: country === 'All' ? 'Total' : country,
      platform: items[0]?.platform || '---',
      historic_coeff: 0,
      arppu: 0, ltv: 0, predicted_ltv: 0, cpa_cost_per_action: 0, cpu_cost_per_user: 0, roi: 0, predicted_roi: 0,
      revenue_activation_count: 0, revenue_activation_purchase_count: 0, revenue_activation: 0,
      costs: 0,
      total_revenue: 0, total_predicted_revenue: 0, count_unique_payers: 0, count_unique_payers_activation: 0,
      revenue_p1: 0, revenue_p1_count: 0, revenue_p2: 0, revenue_p2_count: 0,
      revenue_p3: 0, revenue_p3_count: 0, revenue_p4: 0, revenue_p4_count: 0,
      revenue_p5: 0, revenue_p5_count: 0, revenue_p6: 0, revenue_p6_count: 0,
      revenue_p7: 0, revenue_p7_count: 0, revenue_p8: 0, revenue_p8_count: 0,
      revenue_p9: 0, revenue_p9_count: 0, revenue_p10: 0, revenue_p10_count: 0,
      revenue_p11: 0, revenue_p11_count: 0, revenue_p12: 0, revenue_p12_count: 0,
      
      early_predicted_revenue_p1: 0, early_predicted_revenue_p2: 0, early_predicted_revenue_p3: 0, early_predicted_revenue_p4: 0,
      early_predicted_revenue_p5: 0, early_predicted_revenue_p6: 0, early_predicted_revenue_p7: 0, early_predicted_revenue_p8: 0,
      early_predicted_revenue_p9: 0, early_predicted_revenue_p10: 0, early_predicted_revenue_p11: 0, early_predicted_revenue_p12: 0,
      
      predicted_revenue_p1: 0, predicted_revenue_p2: 0, predicted_revenue_p3: 0, predicted_revenue_p4: 0,
      predicted_revenue_p5: 0, predicted_revenue_p6: 0, predicted_revenue_p7: 0, predicted_revenue_p8: 0,
      predicted_revenue_p9: 0, predicted_revenue_p10: 0, predicted_revenue_p11: 0, predicted_revenue_p12: 0,
      
      predicted_churn_p1: 0, predicted_churn_p2: 0, predicted_churn_p3: 0, predicted_churn_p4: 0,
      predicted_churn_p5: 0, predicted_churn_p6: 0, predicted_churn_p7: 0, predicted_churn_p8: 0,
      predicted_churn_p9: 0, predicted_churn_p10: 0, predicted_churn_p11: 0, predicted_churn_p12: 0,
    };
    
    // КЛЮЧЕВОЕ ИСПРАВЛЕНИЕ: Агрегируем Early Predicted Revenue правильно
    // КЛЮЧЕВОЕ ИСПРАВЛЕНИЕ: Агрегируем Early Predicted Revenue правильно
let totalEarlyPredictedRevenue = 0;
let totalRevenueSum = 0;

items.forEach(item => {
  // Рассчитываем Early Predicted для каждого элемента отдельно
  const itemTotalRevenue = calculateTotalRevenue(item); // уже с VAT корректировкой
  
  // Для отдельных элементов рассчитываем Early Predicted правильно
  const historicCoeff = getHistoricCoeff(item);
  let itemEarlyPredicted;
  if (historicCoeff <= 1.0) {
    const adjustment = (historicCoeff - 1.0) * 0.1;
    itemEarlyPredicted = itemTotalRevenue * (1 + adjustment);
  } else {
    const adjustment = (historicCoeff - 1.0) * 0.1;
    itemEarlyPredicted = itemTotalRevenue * (1 + adjustment);
  }
  
  totalEarlyPredictedRevenue += itemEarlyPredicted;
  totalRevenueSum += itemTotalRevenue;
  
  // Остальная агрегация как обычно
  aggregated.revenue_activation_count += getNumericValue(item.revenue_activation_count);
  (aggregated.revenue_activation_purchase_count as number) += getNumericValue(item.revenue_activation_purchase_count);
  (aggregated.revenue_activation as number) += getAdjustedRevenue(getNumericValue(item.revenue_activation), item.country);
  (aggregated.costs as number) += getNumericValue(item.costs); 
  (aggregated.total_revenue as number) += itemTotalRevenue;
  (aggregated.total_predicted_revenue as number) += calculatePredictedRevenue2(item);
  (aggregated.count_unique_payers as number) += getNumericValue(item.count_unique_payers);
  (aggregated.count_unique_payers_activation as number) += getNumericValue(item.count_unique_payers_activation);

  // Агрегация периодов
  for (let i = 1; i <= 12; i++) {
    const revenueKey = `revenue_p${i}` as keyof OrderData;
    const countKey = `revenue_p${i}_count` as keyof OrderData;
    const predictedKey = `predicted_revenue_p${i}` as keyof OrderData;

    (aggregated as any)[revenueKey] = ((aggregated as any)[revenueKey] || 0) + getAdjustedRevenue(getNumericValue((item as any)[revenueKey]), item.country);
    (aggregated as any)[countKey] = ((aggregated as any)[countKey] || 0) + getNumericValue((item as any)[countKey]);
    (aggregated as any)[predictedKey] = ((aggregated as any)[predictedKey] || 0) + getAdjustedRevenue(getNumericValue((item as any)[predictedKey]), item.country);
    
    // Early predicted periods агрегируем правильно
    const earlyPredictedKey = `early_predicted_revenue_p${i}` as keyof OrderData;
    const enrichedItem = enrichOrderDataWithEarlyPredicted(item);
    const earlyPredictedValue = getNumericValue(enrichedItem[earlyPredictedKey]);
    const adjustedEarlyPredictedValue = getAdjustedRevenue(earlyPredictedValue, item.country);
    (aggregated as any)[earlyPredictedKey] = ((aggregated as any)[earlyPredictedKey] || 0) + adjustedEarlyPredictedValue;
  }
});

// ИСПРАВЛЕНИЕ: Сохраняем правильное значение Early Predicted для агрегированных данных
if (totalRevenueSum > 0 && totalEarlyPredictedRevenue > 0) {
  const impliedAdjustment = (totalEarlyPredictedRevenue / totalRevenueSum) - 1;
  aggregated.historic_coeff = 1 + (impliedAdjustment / 0.1);
  
  // ВАЖНО: Сохраняем значение БЕЗ повторной VAT корректировки, так как оно уже учтено
  (aggregated as any).aggregated_early_predicted_revenue = totalEarlyPredictedRevenue;
} else {
  aggregated.historic_coeff = 1;
  (aggregated as any).aggregated_early_predicted_revenue = totalRevenueSum;
}

    // Пересчитываем производные метрики на основе ИСХОДНОГО total_revenue
    const actualTotalRevenue = totalRevenueSum; // используем правильное значение для расчетов
    const costs = getNumericValue(aggregated.costs);
    const uniquePayers = getNumericValue(aggregated.count_unique_payers);
    const activationPayers = getNumericValue(aggregated.count_unique_payers_activation);
    const purchases = getNumericValue(aggregated.revenue_activation_purchase_count);

    aggregated.arppu = uniquePayers > 0 ? actualTotalRevenue / uniquePayers : 0;
    aggregated.ltv = activationPayers > 0 ? actualTotalRevenue / activationPayers : 0;
    aggregated.cpa_cost_per_action = purchases > 0 ? costs / purchases : 0;
    aggregated.cpu_cost_per_user = uniquePayers > 0 ? costs / uniquePayers : 0;
    aggregated.roi = costs > 0 ? ((actualTotalRevenue - costs) / costs) * 100 : 0;

    const predictedRevenue = (aggregated.total_predicted_revenue as number);
    aggregated.predicted_ltv = activationPayers > 0 ? predictedRevenue / activationPayers : 0;
    aggregated.predicted_roi = costs > 0 ? ((predictedRevenue - costs) / costs) * 100 : 0;

    // Устанавливаем правильное total_revenue для отображения
    (aggregated.total_revenue as number) = actualTotalRevenue;

    return aggregated;
  };

  // =================================================================
  // БЛОК 3: ОСТАЛЬНЫЕ ФУНКЦИИ
  // =================================================================

  const aggregateData = (): AggregatedData => {
    const aggregated: AggregatedData = {};
    
    const allItems: OrderData[] = [];
    Object.values(ordersData).forEach(dateData => {
      Object.values(dateData).forEach(countryItems => {
        allItems.push(...countryItems);
      });
    });

    const filteredItems = allItems.filter(item => {
      const countryMatch = selectedCountries.length === 0 || selectedCountries.includes(item.country);
      const campaignMatch = selectedCampaigns.length === 0 || selectedCampaigns.includes(item.campaign || '---');
      const platformMatch = selectedPlatforms.length === 0 || selectedPlatforms.includes(item.platform || '---');
      return countryMatch && campaignMatch && platformMatch;
    });

    if (showCountries && !showCampaigns) {
      const countryGroups: { [country: string]: OrderData[] } = {};
      filteredItems.forEach(item => {
        if (!countryGroups[item.country]) {
          countryGroups[item.country] = [];
        }
        countryGroups[item.country].push(item);
      });
      Object.entries(countryGroups).forEach(([country, items]) => {
        aggregated[country] = aggregateItems(items, country);
      });
    } else if (!showCountries && showCampaigns) {
      const campaignGroups: { [campaign: string]: OrderData[] } = {};
      filteredItems.forEach(item => {
        const campaign = item.campaign || '---';
        if (!campaignGroups[campaign]) {
          campaignGroups[campaign] = [];
        }
        campaignGroups[campaign].push(item);
      });
      Object.entries(campaignGroups).forEach(([campaign, items]) => {
        aggregated[campaign] = aggregateItems(items, items[0]?.country || 'All');
      });
    } else if (showCountries && showCampaigns) {
      const countryGroups: { [country: string]: OrderData[] } = {};
      filteredItems.forEach(item => {
        if (!countryGroups[item.country]) {
          countryGroups[item.country] = [];
        }
        countryGroups[item.country].push(item);
      });

      Object.entries(countryGroups).forEach(([country, items]) => {
        aggregated[country] = aggregateItems(items, country);
        const campaignGroups: { [campaign: string]: OrderData[] } = {};
        items.forEach(item => {
          const campaign = item.campaign || '---';
          if (!campaignGroups[campaign]) {
            campaignGroups[campaign] = [];
          }
          campaignGroups[campaign].push(item);
        });
        Object.entries(campaignGroups).forEach(([campaign, campaignItems]) => {
          aggregated[`${country}::${campaign}`] = aggregateItems(campaignItems, country);
        });
      });
    } else {
      aggregated['Total'] = aggregateItems(filteredItems, 'All');
    }

    return aggregated;
  };

  const toggleSort = (column: string) => {
    setSortConfig(prevConfig => {
      if (prevConfig.key !== column) {
        return { key: column, direction: 'desc' };
      }
      
      switch (prevConfig.direction) {
        case 'none':
          return { key: column, direction: 'desc' };
        case 'desc':
          return { key: column, direction: 'asc' };
        case 'asc':
          return { key: column, direction: 'none' };
        default:
          return { key: column, direction: 'desc' };
      }
    });
  };

  const renderSortIcon = (column: string): React.ReactNode => {
    if (sortConfig.key !== column) {
      return <ArrowUpDown size={16} className="sort-icon" />;
    }
    
    if (sortConfig.direction === 'desc') {
      return <ArrowDown size={16} className="sort-icon" />;
    }
    
    if (sortConfig.direction === 'asc') {
      return <ArrowUp size={16} className="sort-icon" />;
    }
    
    return <ArrowUpDown size={16} className="sort-icon" />;
  };

  useEffect(() => {
    const fetchOrdersData = async () => {
      setLoading(true);
      
      try {
        let endpoint = 'https://dashboard-nk.golive.mobi/api/web/api/orders';
        const params: Record<string, any> = {};
        
        params.country = showCountries;
        params.distribute_unknowns = distributeUnknowns;
        params.distribute_direct = distributeDirect;
        params.keyevent = viewMode === 'daily' ? 'days' : 'month';
        params.date_from = dateFrom;
        params.date_to = dateTo;
        
        if (selectedCountries.length > 0) {
          params.country_filter = selectedCountries.join(',');
        }
        
        if (selectedCampaigns.length > 0) {
          params.campaign = selectedCampaigns.join(',');
        }

        const queryString = new URLSearchParams(params).toString();
        const response = await fetch(`${endpoint}?${queryString}`);
        
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        
        const responseData = await response.json();
        const orderData = responseData.data || {};
        
        setOrdersData(orderData);
        setError(null);
      } catch (err) {
        console.error('Failed to fetch orders data:', err);
        setError('Failed to fetch orders data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchOrdersData();
  }, [
    selectedCountries, 
    selectedCampaigns, 
    selectedPlatforms,
    dateFrom, 
    dateTo, 
    viewMode, 
    showCountries,
    distributeUnknowns,
    distributeDirect
  ]);

  const renderPeriodColumns = () => {
    if (hideAllPeriods) return [];
    
    const periods = [];
    
    for (let i = 1; i <= 12; i++) {
      periods.push(
        <th key={`P${i}`} style={{ padding: '15px 10px', textAlign: 'right', fontWeight: 600, color: '#6c757d', fontSize: '13px', textTransform: 'uppercase', borderRight: '1px solid #e9ecef', borderBottom: '2px solid #dee2e6', position: 'sticky', top: 0, backgroundColor: '#f8f9fa' }}>
          P{i}
          {showWithTax && ( <span style={{ fontSize: '10px', display: 'block', fontWeight: 'normal', color: '#2c3e50', marginTop: '2px' }}>After VAT</span> )}
        </th>
      );

      if (showEarlyPredicted) {
        periods.push(
          <th key={`Early P${i}`} style={{ padding: '15px 10px', textAlign: 'right', fontWeight: 600, color: '#6c757d', fontSize: '13px', textTransform: 'uppercase', borderRight: '1px solid #e9ecef', borderBottom: '2px solid #dee2e6', backgroundColor: '#fff3e0', position: 'sticky', top: 0 }}>
            Early P{i}
            <span style={{ fontSize: '10px', display: 'block', fontWeight: 'normal', color: '#ff6b35', marginTop: '2px' }}>Early Predicted</span>
            {showWithTax && ( <span style={{ fontSize: '10px', display: 'block', fontWeight: 'normal', color: '#2c3e50', marginTop: '1px' }}>After VAT</span> )}
          </th>
        );
      }
    }
    return periods;
  };

  const renderPeriodValues = (item: OrderData, isGrandTotalRow: boolean = false) => {
    if (hideAllPeriods) return [];
    
    const periodValues = [];
    
    const isAggregatedData = item.country === 'All' || item.campaign === 'Total' || item.campaign === item.country;
    
    let enrichedItem: OrderData;
    if (isAggregatedData) {
      enrichedItem = item;
    } else {
      enrichedItem = enrichOrderDataWithEarlyPredicted(item);
    }
    
    for (let i = 1; i <= 12; i++) {
      const periodKey = `revenue_p${i}` as keyof OrderData;
      const value = enrichedItem[periodKey];
      
      const adjustedValue = isAggregatedData 
        ? getNumericValue(value)
        : getAdjustedRevenue(getNumericValue(value), enrichedItem.country);
      
      periodValues.push(
        <td key={`p${i}`} style={{ 
          padding: '15px 10px', 
          textAlign: 'right', 
          color: isGrandTotalRow ? 'white' : '#495057', 
          borderRight: isGrandTotalRow ? '1px solid #4a627a' : '1px solid #e9ecef', 
          borderBottom: '1px solid #e9ecef'
        }}>
          {adjustedValue.toFixed(2)}
        </td>
      );

      if (showEarlyPredicted) {
        const earlyPredictedKey = `early_predicted_revenue_p${i}` as keyof OrderData;
        
        const earlyPredictedValue = isAggregatedData 
          ? getNumericValue(enrichedItem[earlyPredictedKey])
          : getNumericValue(enrichedItem[earlyPredictedKey]);
        
        const adjustedEarlyPredictedValue = isAggregatedData
          ? earlyPredictedValue
          : getAdjustedRevenue(earlyPredictedValue, enrichedItem.country);
        
        periodValues.push(
          <td key={`early_p${i}`} style={{ 
            padding: '15px 10px', 
            textAlign: 'right', 
            color: isGrandTotalRow ? 'white' : '#495057', 
            borderRight: isGrandTotalRow ? '1px solid #4a627a' : '1px solid #e9ecef', 
            borderBottom: '1px solid #e9ecef', 
            backgroundColor: isGrandTotalRow ? '#4a627a' : '#fff3e0'
          }}>
            {adjustedEarlyPredictedValue.toFixed(2)}
          </td>
        );
      }
    }
    return periodValues;
  };

  const SortableHeaderCell: React.FC<{
    column: string;
    title: string;
    vatAdjusted?: boolean;
    earlyPredicted?: boolean;
    style?: React.CSSProperties;
  }> = ({ column, title, vatAdjusted = false, earlyPredicted = false, style = {} }) => (
    <th 
      onClick={() => toggleSort(column)}
      style={{ padding: '15px 15px', textAlign: 'right', fontWeight: 600, color: '#6c757d', fontSize: '13px', textTransform: 'uppercase', borderRight: '1px solid #e9ecef', borderBottom: '2px solid #dee2e6', cursor: 'pointer', position: 'sticky', top: 0, backgroundColor: sortConfig.key === column ? '#e9f7fe' : (earlyPredicted ? '#fff3e0' : '#f8f9fa'), transition: 'background-color 0.2s ease', ...style }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '5px' }}>
        {title}
        {renderSortIcon(column)}
      </div>
      {vatAdjusted && showWithTax && ( <span style={{ fontSize: '10px', display: 'block', fontWeight: 'normal', color: '#2c3e50', marginTop: '2px' }}>After VAT</span> )}
      {earlyPredicted && ( <span style={{ fontSize: '10px', display: 'block', fontWeight: 'normal', color: '#ff6b35', marginTop: '2px' }}>Early Predicted</span> )}
    </th>
  );
  
  // =================================================================
  // БЛОК 4: СОРТИРОВКА И РЕНДЕР
  // =================================================================

  const aggregatedData = aggregateData();
  
  let sortedEntries: [string, OrderData][] = Object.entries(aggregatedData);

  const grandTotalEntry = ((): [string, OrderData] | null => {
      const allItems: OrderData[] = [];
      Object.values(ordersData).forEach(dateData => {
        Object.values(dateData).forEach(countryItems => {
          allItems.push(...countryItems);
        });
      });

      if (allItems.length === 0) return null;

      const totalData = aggregateItems(allItems, 'All');
      totalData.campaign = 'Total';

      return ['Total', totalData];
  })();

  if (showCountries && showCampaigns) {
    const countries = sortedEntries.filter(([key]) => !key.includes('::') && key !== 'Total');
    const campaigns = sortedEntries.filter(([key]) => key.includes('::'));
    
    if (sortConfig.direction !== 'none' && sortConfig.key) {
      countries.sort(([, a], [, b]) => {
        const aValue = getColumnValue(a, sortConfig.key);
        const bValue = getColumnValue(b, sortConfig.key);
        return sortConfig.direction === 'desc' ? (bValue - aValue) : (aValue - bValue);
      });
    }
    
    const finalSorted: [string, OrderData][] = [];
    countries.forEach(([countryKey, countryData]) => {
      finalSorted.push([countryKey, countryData]);
      const countryCampaigns = campaigns.filter(([key]) => key.startsWith(`${countryKey}::`));
      
      if (sortConfig.direction !== 'none' && sortConfig.key) {
        countryCampaigns.sort(([, a], [, b]) => {
          const aValue = getColumnValue(a, sortConfig.key);
          const bValue = getColumnValue(b, sortConfig.key);
          return sortConfig.direction === 'desc' ? (bValue - aValue) : (aValue - bValue);
        });
      }
      finalSorted.push(...countryCampaigns);
    });
    sortedEntries = finalSorted;
  } else {
    if (sortConfig.direction !== 'none' && sortConfig.key) {
      sortedEntries.sort(([, a], [, b]) => {
        const aValue = getColumnValue(a, sortConfig.key);
        const bValue = getColumnValue(b, sortConfig.key);
        return sortConfig.direction === 'desc' ? (bValue - aValue) : (aValue - bValue);
      });
    }
  }

  if (grandTotalEntry) {
      sortedEntries.unshift(grandTotalEntry);
  }

  const calculateColSpan = (): number => {
    let baseColumns = 14;
    if (!hideAllPeriods) {
      baseColumns += 12;
      if (showEarlyPredicted) {
        baseColumns += 12;
      }
    }
    if (showEarlyPredicted) {
      baseColumns += 3;
    }
    return baseColumns;
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
        <Loader2 size={48} style={{ animation: 'spin 1s linear infinite', color: '#3498db' }} />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 4px 10px rgba(0,0,0,0.06)', margin: '20px 30px', padding: '20px', textAlign: 'center', color: '#dc3545' }}>
        {error}
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 4px 10px rgba(0,0,0,0.06)', margin: '20px 30px', overflow: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0', fontSize: '14px' }}>
        <thead style={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: '#f8f9fa', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
          <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '1px solid #e9ecef' }}>
            <th style={{ padding: '15px 20px', textAlign: 'left', fontWeight: 600, color: '#6c757d', fontSize: '13px', textTransform: 'uppercase', borderRight: '1px solid #e9ecef', borderBottom: '2px solid #dee2e6', position: 'sticky', top: 0, backgroundColor: '#f8f9fa' }}>
              {showCountries && showCampaigns ? 'Country/Campaign' : showCountries ? 'Country' : showCampaigns ? 'Campaign' : 'Total'}
              <span style={{ display: 'block', fontSize: '11px', color: '#6c757d', fontWeight: 'normal', textTransform: 'none' }}>
                {dateFrom} - {dateTo}
              </span>
            </th>
            <th style={{ padding: '15px 15px', textAlign: 'left', fontWeight: 600, color: '#6c757d', fontSize: '13px', textTransform: 'uppercase', borderRight: '1px solid #e9ecef', borderBottom: '2px solid #dee2e6', position: 'sticky', top: 0, backgroundColor: '#f8f9fa' }}>
              Platform
            </th>
            <th style={{ padding: '15px 15px', textAlign: 'right', fontWeight: 600, color: '#6c757d', fontSize: '13px', textTransform: 'uppercase', borderRight: '1px solid #e9ecef', borderBottom: '2px solid #dee2e6', position: 'sticky', top: 0, backgroundColor: '#f8f9fa' }}>
              Revenue Activation Count
            </th>
            <th style={{ padding: '15px 15px', textAlign: 'right', fontWeight: 600, color: '#6c757d', fontSize: '13px', textTransform: 'uppercase', borderRight: '1px solid #e9ecef', borderBottom: '2px solid #dee2e6' }}>
              Revenue Activation Purchase Count
            </th>
            <SortableHeaderCell column="revenue_activation" title="Revenue Activation" vatAdjusted={true} />
            {renderPeriodColumns()}
            <SortableHeaderCell column="costs" title="Cost" />
            <SortableHeaderCell column="total_revenue" title="Total Revenue" vatAdjusted={true} style={{ backgroundColor: sortConfig.key === 'total_revenue' ? '#d4ebfa' : '#e9f7fe' }} />
            <SortableHeaderCell column="profit" title="Profit" style={{ backgroundColor: sortConfig.key === 'profit' ? '#d4f5d4' : '#f0fff0' }} />
            <SortableHeaderCell column="roi" title="ROI (%)" />
            <SortableHeaderCell column="predicted_roi" title="Predicted ROI (%)" />
            <th style={{ padding: '15px 15px', textAlign: 'right', fontWeight: 600, color: '#6c757d', fontSize: '13px', textTransform: 'uppercase', borderRight: '1px solid #e9ecef', borderBottom: '2px solid #dee2e6', backgroundColor: '#e9f7fe', position: 'sticky', top: 0 }}>
              Total Predicted Revenue
              {showWithTax && ( <span style={{ fontSize: '10px', display: 'block', fontWeight: 'normal', color: '#2c3e50', marginTop: '2px' }}>After VAT</span> )}
            </th>
            {showEarlyPredicted && (
              <>
                <SortableHeaderCell column="early_predicted_roi" title="Early Predicted ROI (%)" earlyPredicted={true} />
                <SortableHeaderCell column="total_early_predicted_revenue" title="Total Early Predicted Revenue" vatAdjusted={true} earlyPredicted={true} />
                <th style={{ padding: '15px 15px', textAlign: 'right', fontWeight: 600, color: '#6c757d', fontSize: '13px', textTransform: 'uppercase', borderRight: '1px solid #e9ecef', borderBottom: '2px solid #dee2e6', backgroundColor: '#fff3e0', position: 'sticky', top: 0 }}>
                  Early Predicted LTV
                  <span style={{ fontSize: '10px', display: 'block', fontWeight: 'normal', color: '#ff6b35', marginTop: '2px' }}>Early Predicted</span>
                  {showWithTax && ( <span style={{ fontSize: '10px', display: 'block', fontWeight: 'normal', color: '#2c3e50', marginTop: '1px' }}>After VAT</span> )}
                </th>
              </>
            )}
            <SortableHeaderCell column="cpa_cost_per_action" title="CPA" />
            <SortableHeaderCell column="cpu_cost_per_user" title="CPU" />
            <th style={{ padding: '15px 15px', textAlign: 'right', fontWeight: 600, color: '#6c757d', fontSize: '13px', textTransform: 'uppercase', borderRight: '1px solid #e9ecef', borderBottom: '2px solid #dee2e6' }}>
              LTV
            </th>
            <th style={{ padding: '15px 15px', textAlign: 'right', fontWeight: 600, color: '#6c757d', fontSize: '13px', textTransform: 'uppercase', borderRight: '1px solid #e9ecef', borderBottom: '2px solid #dee2e6' }}>
              Predicted LTV
            </th>
            <th style={{ padding: '15px 15px', textAlign: 'right', fontWeight: 600, color: '#6c757d', fontSize: '13px', textTransform: 'uppercase', borderBottom: '2px solid #dee2e6', position: 'sticky', top: 0, backgroundColor: '#f8f9fa' }}>
              ARPPU
            </th>
          </tr>
        </thead>
        <tbody>
          {sortedEntries.map(([key, item]) => {
            const isCountryRow = !key.includes('::') && key !== 'Total';
            const isGrandTotalRow = key === 'Total';
            const displayName = key.includes('::') ? 
              key.split('::')[1] : 
              key;
            
            return (
              <tr 
                key={key}
                style={{
                  borderBottom: '1px solid #e9ecef',
                  transition: 'background-color 0.2s ease',
                  backgroundColor: isGrandTotalRow ? '#34495e' : (isCountryRow ? '#f8f9fa' : 'transparent'),
                  color: isGrandTotalRow ? 'white' : 'inherit',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = isGrandTotalRow ? '#4a627a' : (isCountryRow ? '#f1f3f5' : '#f5f5f5');
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = isGrandTotalRow ? '#34495e' : (isCountryRow ? '#f8f9fa' : 'transparent');
                }}
              >
                <td style={{
                  padding: '15px 20px',
                  fontWeight: isCountryRow || isGrandTotalRow ? 600 : 'normal',
                  borderRight: '1px solid #e9ecef',
                  borderBottom: '1px solid #e9ecef',
                  paddingLeft: isCountryRow || isGrandTotalRow ? '20px' : '40px'
                }}>
                  {displayName}
                </td>
                
                <td style={{ padding: '15px 15px', textAlign: 'left', borderRight: '1px solid #e9ecef', borderBottom: '1px solid #e9ecef' }}>
                  {isCountryRow || isGrandTotalRow ? '' : (item.platform || '---')}
                </td>
                
                <td style={{ padding: '15px 10px', textAlign: 'right', borderRight: '1px solid #e9ecef', borderBottom: '1px solid #e9ecef' }}>
                  {(getNumericValue(item.revenue_activation_count)).toLocaleString()}
                </td>
                <td style={{ padding: '15px 10px', textAlign: 'right', borderRight: '1px solid #e9ecef', borderBottom: '1px solid #e9ecef' }}>
                  {(getNumericValue(item.revenue_activation_purchase_count)).toLocaleString()}
                </td>
                <td style={{ padding: '15px 10px', textAlign: 'right', borderRight: '1px solid #e9ecef', borderBottom: '1px solid #e9ecef' }}>
                  {getAdjustedRevenue(getNumericValue(item.revenue_activation), item.country).toFixed(2)}
                </td>
                
                {renderPeriodValues(item, isGrandTotalRow)}
                
                <td style={{ padding: '15px 10px', textAlign: 'right', borderRight: '1px solid #e9ecef', borderBottom: '1px solid #e9ecef' }}>
                  {getNumericValue(item.costs).toFixed(2)}
                </td>
                
                <td style={{ padding: '15px 15px', textAlign: 'right', borderRight: '1px solid #e9ecef', borderBottom: '1px solid #e9ecef', backgroundColor: isGrandTotalRow ? '#2c3e50' : '#e9f7fe', fontWeight: 600 }}>
                  {calculateTotalRevenue(item).toFixed(2)}
                </td>

                <td style={{ padding: '15px 15px', textAlign: 'right', borderRight: '1px solid #e9ecef', borderBottom: '1px solid #e9ecef', backgroundColor: isGrandTotalRow ? '#2c3e50' : '#f0fff0', fontWeight: 600 }}>
                  {(calculateTotalRevenue(item) - getNumericValue(item.costs)).toFixed(2)}
                </td>
                
                <td style={{ padding: '15px 10px', textAlign: 'right', borderRight: '1px solid #e9ecef', borderBottom: '1px solid #e9ecef' }}>
                  {calculateROI(item).toFixed(2)}%
                </td>
                
                <td style={{ padding: '15px 10px', textAlign: 'right', borderRight: '1px solid #e9ecef', borderBottom: '1px solid #e9ecef' }}>
                  {calculatePredictedROI(item).toFixed(2)}%
                </td>
                
                <td style={{ padding: '15px 15px', textAlign: 'right', borderRight: '1px solid #e9ecef', borderBottom: '1px solid #e9ecef', backgroundColor: isGrandTotalRow ? '#2c3e50' : '#e9f7fe', fontWeight: 600 }}>
                  {calculatePredictedRevenue2(item).toFixed(2)}
                </td>

                {showEarlyPredicted && (
                  <>
                    <td style={{ padding: '15px 10px', textAlign: 'right', borderRight: '1px solid #e9ecef', borderBottom: '1px solid #e9ecef', backgroundColor: isGrandTotalRow ? '#4a627a' : '#fff3e0' }}>
                      {calculateEarlyPredictedROI(item).toFixed(2)}%
                    </td>
                    
                    <td style={{ padding: '15px 15px', textAlign: 'right', borderRight: '1px solid #e9ecef', borderBottom: '1px solid #e9ecef', backgroundColor: isGrandTotalRow ? '#4a627a' : '#fff3e0', fontWeight: 600 }}>
                      {calculateEarlyPredictedRevenue(item).toFixed(2)}
                    </td>
                    
                    <td style={{ padding: '15px 10px', textAlign: 'right', borderRight: '1px solid #e9ecef', borderBottom: '1px solid #e9ecef', backgroundColor: isGrandTotalRow ? '#4a627a' : '#fff3e0' }}>
                      {calculateEarlyPredictedLTV(item).toFixed(2)}
                    </td>
                  </>
                )}
                
                <td style={{ padding: '15px 10px', textAlign: 'right', borderRight: '1px solid #e9ecef', borderBottom: '1px solid #e9ecef' }}>
                  {calculateCPA(item).toFixed(2)}
                </td>
                
                <td style={{ padding: '15px 10px', textAlign: 'right', borderRight: '1px solid #e9ecef', borderBottom: '1px solid #e9ecef' }}>
                  {calculateCPU(item).toFixed(2)}
                </td>
                
                <td style={{ padding: '15px 10px', textAlign: 'right', borderRight: '1px solid #e9ecef', borderBottom: '1px solid #e9ecef' }}>
                  {calculateLTV(item).toFixed(2)}
                </td>
                
                <td style={{ padding: '15px 10px', textAlign: 'right', borderRight: '1px solid #e9ecef', borderBottom: '1px solid #e9ecef' }}>
                  {calculatePredictedLTV(item).toFixed(2)}
                </td>
                
                <td style={{ padding: '15px 10px', textAlign: 'right', borderBottom: '1px solid #e9ecef' }}>
                  {calculateARPPU(item).toFixed(2)}
                </td>
              </tr>
            );
          })}
          
          {sortedEntries.length === 0 && (
            <tr>
              <td colSpan={calculateColSpan()} style={{ padding: '30px 20px', textAlign: 'center', color: '#6c757d' }}>
                No data available for the selected filters
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default TotalsComponent;